import styles from './ApprovedElement.module.scss';
import { DISPLAY_TYPES, ELEMENTS } from 'Editor/services/consts';
import { BaseBlockElement } from '../../../ViewBuilders';

export class ApprovedViewElement extends BaseBlockElement {
  private overlay?: HTMLElement;
  private content?: HTMLElement;
  #userId?: UserId;
  constructor(Visualizer?: Editor.Visualizer.State, Data?: Editor.Data.API) {
    super(Visualizer, Data, { doNeedBaseTooltip: false });

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
  }

  static onClickListener(e: Event) {
    e.stopPropagation();
  }

  static onMouseUpListener(e: Event) {
    e.stopPropagation();
  }

  static get observedAttributes() {
    return [
      ...BaseBlockElement.observedAttributes,
      'data-space-after',
      'data-space-before',
      'data-asb',
      'data-asa',
    ];
  }

  get userId(): string {
    return this.#userId ?? '';
  }

  set userId(value: string) {
    this.#userId = value;
  }

  get displayType() {
    return DISPLAY_TYPES.BLOCK;
  }

  get isEditable() {
    return false;
  }

  get isDeletable() {
    return false;
  }

  get isSplitable() {
    return false;
  }

  get isContentWrapper() {
    return true;
  }

  get isSelectable() {
    return true;
  }

  onMouseEnter() {
    const widgetsManager = this.Visualizer?.getWidgetsManager?.();
    if (widgetsManager) {
      widgetsManager.addWidget('tooltip', this, {
        content: 'CONTENT_APPROVED_BY',
        userId: this.userId,
        forceVertical: true,
        testId: this.id,
      });
    }
  }

  onMouseLeave() {
    const widgetsManager = this.Visualizer?.getWidgetsManager?.();
    if (widgetsManager) {
      widgetsManager.removeWidget('tooltip', this.id);
    }
  }

  attributeChangedCallback(attribute: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      switch (attribute) {
        case 'data-space-after':
        case 'data-space-before':
        case 'data-asa':
        case 'data-asb':
          this.handleSpacing();
          break;
        default:
          super.attributeChangedCallback(attribute, oldValue, newValue);
          break;
      }
    }
  }

  private handleSpacing() {
    if (this.dataset.asb === 'true') {
      this.style.marginTop = '14pt';
    } else if (this.dataset.spaceBefore != null && this.dataset.spaceBefore !== '') {
      this.style.marginTop = this.dataset.spaceBefore ? `${this.dataset.spaceBefore}pt` : '';
    } else if (this.dataset.asb === 'false') {
      this.style.marginTop = '0pt';
    } else {
      this.style.marginTop = '';
    }

    if (this.dataset.asa === 'true') {
      this.style.marginBottom = '14pt';
    } else if (this.dataset.spaceAfter != null && this.dataset.spaceAfter !== '') {
      this.style.marginBottom = this.dataset.spaceAfter ? `${this.dataset.spaceAfter}pt` : '';
    } else if (this.dataset.asa === 'false') {
      this.style.marginBottom = '0pt';
    } else {
      this.style.marginBottom = '';
    }
  }

  get contentContainer(): Element | null {
    if (this.content) {
      return this.content;
    } else {
      const content = this.querySelector('[data-content-container="true"]');
      if (content) {
        return content;
      }
    }
    return null;
  }

  get selectableContent(): Node | null {
    if (this.content?.childNodes[0]) {
      return this.content.childNodes[0];
    } else {
      const content = this.querySelector('[data-content-container="true"]');
      if (content?.childNodes[0]) {
        return content.childNodes[0];
      }
    }

    return null;
  }

  setupStructure(
    nodeContent: HTMLElement,
    layoutType: Editor.Visualizer.LayoutType,
    approvingUsers?: any[],
    userPermissions?: string[],
  ) {
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }

    this.userId = approvingUsers?.[0].user || '';

    this.setAttribute('spellcheck', 'false');

    this.overlay = document.createElement('div');
    this.overlay.setAttribute('class', styles.overlay);
    this.overlay.setAttribute('contenteditable', 'false');

    this.content = document.createElement('div');
    this.content.setAttribute('class', styles.content);
    this.content.dataset.contentContainer = 'true';

    if (nodeContent) {
      this.content.appendChild(nodeContent);
    }

    this.appendChild(this.overlay);
    this.appendChild(this.content);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', ApprovedViewElement.onClickListener);
    this.addEventListener('mouseup', ApprovedViewElement.onMouseUpListener);

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('click', ApprovedViewElement.onClickListener);
    this.removeEventListener('mouseup', ApprovedViewElement.onMouseUpListener);

    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    super.disconnectedCallback();
  }
}

if (!window.customElements.get(ELEMENTS.ApprovedElement.IDENTIFIER)) {
  window.customElements.define(ELEMENTS.ApprovedElement.IDENTIFIER, ApprovedViewElement);
}
